import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Hook headless que separa la lógica del timer del UI del toast.
 *
 * Características:
 *  - Timer que se pausa cuando el cursor está sobre la notificación.
 *  - Reanuda correctamente desde el tiempo restante (no reinicia).
 *  - Limpia todos los timers al desmontar el componente.
 *  - Soporta modo persistente (sin auto-dismiss).
 *  - Cierre por teclado (Escape).
 */
export function useNotificationTimer({ id, duration, persistent, onDismiss }) {
  const [exiting, setExiting] = useState(false);
  const [hovered, setHovered] = useState(false);

  const autoTimerRef = useRef(null);
  const exitTimerRef = useRef(null);
  const remainingRef = useRef(duration);
  const startedAtRef = useRef(null);

  const dismiss = useCallback(() => {
    setExiting(true);
    clearTimeout(exitTimerRef.current);
    exitTimerRef.current = setTimeout(() => onDismiss(id), 280);
  }, [id, onDismiss]);

  const stopTimer = useCallback(() => {
    if (!autoTimerRef.current) return;
    clearTimeout(autoTimerRef.current);
    autoTimerRef.current = null;
    remainingRef.current -= Date.now() - (startedAtRef.current ?? Date.now());
  }, []);

  const startTimer = useCallback(() => {
    if (persistent || remainingRef.current <= 0) return;
    startedAtRef.current = Date.now();
    autoTimerRef.current = setTimeout(dismiss, remainingRef.current);
  }, [dismiss, persistent]);

  useEffect(() => {
    startTimer();
    return () => {
      clearTimeout(autoTimerRef.current);
      clearTimeout(exitTimerRef.current);
    };
  }, [startTimer]);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    stopTimer();
  }, [stopTimer]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    startTimer();
  }, [startTimer]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') dismiss();
  }, [dismiss]);

  return {
    exiting,
    hovered,
    dismiss,
    handleMouseEnter,
    handleMouseLeave,
    handleKeyDown,
  };
}
